/**
 * « Performances over time » — Ads KPIs jour par jour, sept jours à la fois.
 *
 * Fenêtre PROPRE au tableau, indépendante de la période de la page : on y
 * cherche « qu'est-ce qui s'est passé mardi ? », pas un total. Les flèches
 * reculent ou avancent d'une semaine ; on n'avance pas au-delà d'hier.
 *
 * Suit le sélecteur de plateformes : chaque jour additionne les plateformes
 * cochées, et les taux (CTR, CVR, ROAS, CPC, CPM, CPA) sont recalculés sur
 * ces sommes.
 */

import type { AdsDayFigures } from '@shared/types/sales';
import { adsRoas } from '@shared/types/sales';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { useAuth } from '../hooks/useAuth';
import { ApiRequestError, fetchAdsDailyKpis } from '../lib/api';
import { shiftDay } from '../lib/dates';
import { formatDayLong, formatDecimal, formatInteger, formatMoney, formatPercent } from '../lib/format';
import { Card } from './Card';

interface AdsDailyTableProps {
  withMeta: boolean;
  withAmazon: boolean;
  withGoogle: boolean;
  currency: string;
}

const ratioFormatter = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const weekdayFormatter = new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'UTC' });

const empty: AdsDayFigures = { cost: 0, impressions: 0, clicks: 0, conversions: 0, conversionValue: 0 };
const add = (a: AdsDayFigures, b: AdsDayFigures | null | undefined): AdsDayFigures =>
  b
    ? {
        cost: a.cost + b.cost,
        impressions: a.impressions + b.impressions,
        clicks: a.clicks + b.clicks,
        conversions: a.conversions + b.conversions,
        conversionValue: a.conversionValue + b.conversionValue,
      }
    : a;

export function AdsDailyTable({ withMeta, withAmazon, withGoogle, currency }: AdsDailyTableProps) {
  const { token, signOut } = useAuth();
  // `null` : la semaine qui finit aujourd'hui. Sinon, le dernier jour affiché.
  const [to, setTo] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['ads-daily', to],
    queryFn: async ({ signal }) => {
      if (!token) throw new ApiRequestError(401, 'Session expiree');
      try {
        return await fetchAdsDailyKpis(to, token, signal);
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 401) signOut();
        throw error;
      }
    },
    enabled: Boolean(token),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const data = query.data;
  const rows = (data?.days ?? []).map((day) => ({
    date: day.date,
    figures: add(add(add(empty, withMeta ? day.meta : null), withAmazon ? day.amazon : null), withGoogle ? day.google : null),
  }));
  const total = rows.reduce((acc, row) => add(acc, row.figures), empty);
  const atLatest = !data || data.to >= data.latestDay;

  const money = (value: number, cents = false) => formatMoney(value, currency, cents);
  const cells = (f: AdsDayFigures) => [
    money(f.cost),
    formatInteger(f.impressions),
    formatInteger(f.clicks),
    f.impressions > 0 ? formatPercent((f.clicks / f.impressions) * 100) : '—',
    formatDecimal(f.conversions),
    f.clicks > 0 ? formatPercent((f.conversions / f.clicks) * 100) : '—',
    money(f.conversionValue),
    adsRoas(f.conversionValue, f.cost) !== null ? `×${ratioFormatter.format(adsRoas(f.conversionValue, f.cost) ?? 0)}` : '—',
    f.clicks > 0 ? money(f.cost / f.clicks, true) : '—',
    f.impressions > 0 ? money((f.cost / f.impressions) * 1000, true) : '—',
    f.conversions > 0 ? money(f.cost / f.conversions, true) : '—',
  ];
  const HEADERS = ['Cost HT', 'Impressions', 'Clicks', 'CTR %', 'Conversions', 'CVR %', 'Conversion Value TTC', 'ROAS HT', 'CPC HT', 'CPM HT', 'CPA HT'];

  return (
    <Card
      title="Performances over time"
      subtitle={data ? `Du ${formatDayLong(data.from)} au ${formatDayLong(data.to)} — un jour par ligne` : 'Un jour par ligne'}
      className="chart-grid__wide"
    >
      <div className="week-pager" role="group" aria-label="Semaine affichée">
        <button
          type="button"
          className="btn btn--small"
          onClick={() => data && setTo(shiftDay(data.from, -1))}
          disabled={!data || query.isFetching}
        >
          ← 7 jours précédents
        </button>
        <button
          type="button"
          className="btn btn--small"
          onClick={() => {
            if (!data) return;
            const next = shiftDay(data.to, 7);
            setTo(next >= data.latestDay ? null : next);
          }}
          disabled={atLatest || query.isFetching}
        >
          7 jours suivants →
        </button>
        {to !== null && (
          <button type="button" className="btn btn--small" onClick={() => setTo(null)} disabled={query.isFetching}>
            Revenir aux 7 derniers jours
          </button>
        )}
      </div>

      {query.error && (
        <p className="notice notice--error" role="alert">
          {query.error.message}
        </p>
      )}

      <div className={query.isFetching ? 'table-wrap is-refetching' : 'table-wrap'}>
        <table className="data">
          <thead>
            <tr>
              <th>Date</th>
              {HEADERS.map((header) => (
                <th key={header} className="num">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.date}>
                <td>{weekdayFormatter.format(new Date(`${row.date}T00:00:00Z`))}</td>
                {cells(row.figures).map((value, index) => (
                  <td key={HEADERS[index]} className="num">
                    {value}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length > 0 && (
              <tr>
                <td>
                  <strong>7 jours</strong>
                </td>
                {cells(total).map((value, index) => (
                  <td key={HEADERS[index]} className="num">
                    <strong>{value}</strong>
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
